import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Edit } from '@mui/icons-material';
import CreateExecutiveGroup from './CreateExecutiveGroup';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import EditExecutiveGroup from './EditExecutiveGroup';
import { IExecutiveGroup } from './types';

function ExecutiveGroupList() {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [exeutiveMembers, setExeutiveMembers] = useState<
    IExecutiveGroup[] | null
  >(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const token = localStorage.getItem(TOKEN_KEY);

  // ▶ Create Group Modal state
  const [createOpen, setCreateOpen] = useState(false);

  const handleCreateOpen = () => setCreateOpen(true);
  const handleCreateClose = () => setCreateOpen(false);

  // ▶ Delete Modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<IExecutiveGroup | null>(
    null
  );

  const [editOpen, setEditOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<IExecutiveGroup | null>(null);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        const uri = `${url}/api/v1/admin/executive/groups`;
        const { data } = await axios.get(uri, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (data.success) {
          setExeutiveMembers(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch groups', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [refreshTrigger]);

  const handleOpenDelete = (group: IExecutiveGroup) => {
    setSelectedGroup(group);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedGroup(null);
  };

  const handleDelete = async () => {
    if (!selectedGroup?.id) return;
    try {
      setDeleteLoading(true);

      await axios.delete(
        `${url}/api/v1/admin/executive/groups/${selectedGroup.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Refresh list
      setRefreshTrigger((prev) => prev + 1);
      handleCloseDelete();
    } catch (error) {
      console.error('Failed to delete group', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Container maxWidth='xl'>
      <Card>
        <CardHeader
          action={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Button
                onClick={handleCreateOpen}
                variant='contained'
                sx={{ ml: 2 }}
              >
                Create Group
              </Button>
            </Box>
          }
          title='Executive Group List'
        />
        <Divider />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SL.</TableCell>
                <TableCell>Group </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            {loading ? (
              <TableLoader />
            ) : exeutiveMembers?.length ? (
              <TableBody>
                {exeutiveMembers.map(
                  (exeutiveMember: IExecutiveGroup, i: number) => (
                    <TableRow hover key={exeutiveMember.id || i}>
                      <TableCell>
                        <Typography
                          variant='body1'
                          color='text.primary'
                          gutterBottom
                          noWrap
                        >
                          {i + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant='body1'
                          color='text.primary'
                          gutterBottom
                          noWrap
                        >
                          {exeutiveMember?.duration}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant='body1'
                          color='text.primary'
                          gutterBottom
                          noWrap
                        >
                          {exeutiveMember?.status ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {/* EDIT – ready when you implement UpdateExecutiveGroup */}
                        <Tooltip title='Edit' arrow>
                          <IconButton
                            sx={{
                              '&:hover': {
                                background: theme.colors.primary.lighter,
                              },
                              color: theme.palette.primary.main,
                            }}
                            color='inherit'
                            size='small'
                            onClick={() => {
                              setEditGroup(exeutiveMember);
                              setEditOpen(true);
                            }}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </Tooltip>

                        {/* DELETE */}
                        <Tooltip title='Delete' arrow>
                          <IconButton
                            onClick={() => handleOpenDelete(exeutiveMember)}
                            sx={{
                              '&:hover': {
                                background: theme.colors.error.lighter,
                              },
                              color: theme.palette.error.main,
                            }}
                            color='inherit'
                            size='small'
                          >
                            <DeleteTwoToneIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            ) : (
              <TableNotFound />
            )}
          </Table>
        </TableContainer>

        {/* CREATE GROUP MODAL */}
        <ModalsWrapper
          setShowModal={setCreateOpen}
          showModal={createOpen}
          modalData={{ title: 'Create Executive Group' }}
          maxWidth='sm'
        >
          <CreateExecutiveGroup
            handleClose={handleCreateClose}
            setRefreshTrigger={setRefreshTrigger}
          />
        </ModalsWrapper>

        {/* DELETE CONFIRMATION MODAL */}
        <DeleteConfirmation
          open={deleteOpen}
          handleClose={handleCloseDelete}
          text={
            selectedGroup
              ? `Want to delete "${selectedGroup.duration}" group?`
              : 'Want to delete this group?'
          }
          handleDeleteFun={handleDelete}
          loading={deleteLoading}
        />

        {/* (Optional) EDIT MODAL – when you create UpdateExecutiveGroup */}

        <ModalsWrapper
          setShowModal={setEditOpen}
          showModal={editOpen}
          modalData={{ title: 'Edit Executive Group' }}
          maxWidth='sm'
        >
          {editGroup && (
            <EditExecutiveGroup
              handleClose={() => setEditOpen(false)}
              setRefreshTrigger={setRefreshTrigger}
              group={editGroup}
            />
          )}
        </ModalsWrapper>
      </Card>
    </Container>
  );
}

export default ExecutiveGroupList;
